import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCheckCircle,
  FaCommentDots,
  FaSpinner,
  FaWhatsapp,
  FaShieldAlt,
  FaCar,
} from 'react-icons/fa';
import bridgeService, { TIER_TO_OPTION } from '../../services/bridgeApi';

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

const PAYMENT_METHODS = [
  { id: 'upi', label: 'UPI' },
  { id: 'card', label: 'Card' },
  { id: 'netbanking', label: 'Net Banking' },
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * CV-01 / CV-02 / CV-03 — Customer Bridge parts approval + payment (no login).
 * Route: /bridge/:token
 */
const BridgeApproval = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [job, setJob] = useState(null);
  const [step, setStep] = useState('landing');
  const [partIndex, setPartIndex] = useState(0);
  const [selections, setSelections] = useState({});
  const [selectedTier, setSelectedTier] = useState('');
  const [summary, setSummary] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [payLoading, setPayLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const idempotencyKey = useMemo(
    () => (job?.jobCardId ? `bridge-pay-${job.jobCardId}` : ''),
    [job?.jobCardId]
  );

  const loadJob = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await bridgeService.getJobCard(token);
      setJob(data);
      if (data.partsPaid) {
        setStep('success');
        setSuccessData({ orderId: '—', amount: 0, alreadyPaid: true });
      } else if (data.partsApproved) {
        setStep('summary');
        const sum = await bridgeService.getPaymentSummary(token);
        setSummary(sum);
      } else {
        setStep('landing');
      }
    } catch (err) {
      if (err.status === 401 || /expired/i.test(err.message || '')) {
        setStep('expired');
      } else {
        setError(err.message || 'Unable to load approval link');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const parts = job?.parts || [];
  const hasParts = parts.length > 0;
  const currentPart = parts[partIndex];
  const garageLabel = job?.garageCity ? `${job.garageCity}` : 'Your';

  const whatsAppGarageUrl = useMemo(() => {
    const phone = (job?.garagePhone || '').replace(/\D/g, '');
    const partName = currentPart?.plainName || 'a part';
    const msg = encodeURIComponent(
      `Hi, I have a question about "${partName}" for my vehicle (${job?.vehicle?.registrationNumber || 'my car'}). Can you help?`
    );
    return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  }, [job, currentPart]);

  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
  };

  const handleNextPart = () => {
    if (!currentPart || !selectedTier) return;
    const optionKey = TIER_TO_OPTION[selectedTier];
    if (!optionKey) return;

    const nextSelections = {
      ...selections,
      [currentPart.partRequestId]: optionKey,
    };
    setSelections(nextSelections);
    setSelectedTier('');

    if (partIndex < parts.length - 1) {
      setPartIndex(partIndex + 1);
    } else {
      submitApprovals(nextSelections);
    }
  };

  const submitApprovals = async (finalSelections) => {
    setPayLoading(true);
    try {
      const choices = Object.entries(finalSelections).map(([partRequestId, chosenOption]) => ({
        partRequestId,
        chosenOption,
      }));
      await bridgeService.approveParts(token, {
        jobCardId: job.jobCardId,
        choices,
      });
      const sum = await bridgeService.getPaymentSummary(token);
      setSummary(sum);
      setStep('summary');
    } catch (err) {
      if (err.code === 'OPTION_OUT_OF_STOCK' && err.parts?.length) {
        setError('A chosen option is no longer available. Please pick again.');
        setStep('parts');
        setPartIndex(0);
        setSelections({});
        setSelectedTier('');
      } else if (err.code === 'ALREADY_APPROVED') {
        const sum = await bridgeService.getPaymentSummary(token);
        setSummary(sum);
        setStep('summary');
      } else {
        setError(err.message || 'Could not save your choices');
      }
    } finally {
      setPayLoading(false);
    }
  };

  const handlePay = async () => {
    if (!token || !idempotencyKey) return;
    setPayLoading(true);
    setError('');
    try {
      const paymentData = await bridgeService.initiatePayment(token, idempotencyKey);
      const { keyId, orderId, amountPaise, amount, currency, totals } = paymentData;

      await loadRazorpayScript();

      if (keyId && orderId && window.Razorpay) {

        const options = {
          key: keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: amountPaise || Math.round((amount || 0) * 100),
          currency: currency || 'INR',
          name: 'SparePe',
          description: `Parts — Job ${job?.jobCardId || ''}`,
          order_id: orderId,
          method: paymentMethod === 'upi' ? { upi: true } : undefined,
          handler: async (res) => {
            try {
              const result = await bridgeService.verifyPayment(token, {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              setSuccessData(result);
              setStep('success');
            } catch (verifyErr) {
              setError(verifyErr.message || 'Payment verification failed');
            } finally {
              setPayLoading(false);
            }
          },
          modal: { ondismiss: () => setPayLoading(false) },
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', () => {
          setPayLoading(false);
          setError('Payment failed or was cancelled.');
        });
        rzp.open();
        return;
      }

      // Demo fallback when Razorpay not configured
      const demoPaymentId = `pay_demo_${Date.now()}`;
      const demoOrderId = orderId || `order_${Date.now()}`;
      let demoSig = `demo_${demoOrderId}_${demoPaymentId}`;
      try {
        const demoRes = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payment/demo-signature`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              razorpay_order_id: demoOrderId,
              razorpay_payment_id: demoPaymentId,
            }),
          }
        );
        const demoBody = await demoRes.json();
        if (demoBody?.data?.signature) demoSig = demoBody.data.signature;
      } catch (_) {
        /* use demo prefix fallback */
      }

      const result = await bridgeService.verifyPayment(token, {
        razorpay_order_id: demoOrderId,
        razorpay_payment_id: demoPaymentId,
        razorpay_signature: demoSig,
      });
      setSuccessData({ ...result, amount: totals?.grandTotal || amount });
      setStep('success');
    } catch (err) {
      setError(err.message || 'Payment could not be started');
    } finally {
      setPayLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="flex items-center gap-2 text-slate-600">
          <FaSpinner className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (step === 'expired') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl bg-white shadow-sm border border-slate-200 p-8 text-center">
          <p className="text-lg font-semibold text-slate-900 mb-2">Link expired</p>
          <p className="text-slate-600 text-sm">
            This link has expired. Contact your garage for a new link.
          </p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-emerald-50 flex flex-col items-center justify-center p-6">
        <FaCheckCircle className="text-5xl text-emerald-500 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-2">Payment successful</h1>
        <p className="text-slate-600 text-sm text-center mb-6">
          {successData?.alreadyPaid
            ? 'Your parts payment was already completed.'
            : 'SparePe order created. WhatsApp confirmation sent.'}
        </p>
        {successData?.orderId && (
          <p className="text-sm text-slate-500 mb-6">Order: {successData.orderId}</p>
        )}
        <a
          href="https://wa.me/"
          className="inline-flex items-center gap-2 rounded-xl bg-green-600 text-white px-6 py-3 font-semibold text-sm"
        >
          <FaWhatsapp /> Return to WhatsApp
        </a>
      </div>
    );
  }

  const totals = summary?.totals || {};
  const summaryLines = summary?.summary || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-lg mx-auto">
          <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">SparePe</p>
          {step === 'landing' && (
            <h1 className="text-base font-bold text-slate-900 leading-snug mt-0.5">
              {garageLabel} garage needs your approval
            </h1>
          )}
          {step === 'parts' && hasParts && (
            <h1 className="text-base font-bold text-slate-900 mt-0.5">
              Choose option {partIndex + 1} of {parts.length}
            </h1>
          )}
          {step === 'parts' && !hasParts && (
            <h1 className="text-base font-bold text-slate-900 mt-0.5">No parts to approve</h1>
          )}
          {step === 'summary' && (
            <h1 className="text-base font-bold text-slate-900 mt-0.5">Review &amp; pay</h1>
          )}
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-5">
        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">
            {error}
          </div>
        )}

        {/* CV-01 Landing */}
        {step === 'landing' && job && (
          <div className="space-y-5">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <FaCar className="text-2xl text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-900">
                    {[job.vehicle?.make, job.vehicle?.model, job.vehicle?.year]
                      .filter(Boolean)
                      .join(' ') || 'Your vehicle'}
                  </p>
                  {job.vehicle?.registrationNumber && (
                    <p className="text-sm text-slate-500">{job.vehicle.registrationNumber}</p>
                  )}
                </div>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-medium text-slate-500 uppercase mb-1">Issue diagnosed</p>
                <p className="text-sm text-slate-700">{job.diagnosisSummary}</p>
              </div>
              <div className="mt-4 flex items-center justify-between rounded-xl bg-amber-50 px-4 py-3">
                <span className="text-sm text-amber-900">Parts to approve</span>
                <span className="text-lg font-bold text-amber-700">{job.partsCount}</span>
              </div>
              {!hasParts && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Your garage has not added any spare parts to this quotation yet. Ask them to add parts, then open this link again.
                </div>
              )}
            </div>

            <button
              type="button"
              disabled={!hasParts}
              onClick={() => hasParts && setStep('parts')}
              className="w-full rounded-xl bg-red-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-4 font-bold text-base shadow-sm active:scale-[0.99] transition"
            >
              Review and Approve
            </button>
            {!hasParts && (
              <a
                href={whatsAppGarageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full rounded-xl border border-green-300 bg-green-50 text-green-800 py-3 text-sm font-semibold"
              >
                <FaCommentDots /> Ask garage to add parts
              </a>
            )}
          </div>
        )}

        {/* CV-02 Per-part choice */}
        {step === 'parts' && !currentPart && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 text-center space-y-4">
            <p className="text-slate-700 text-sm">
              There are no parts waiting for your approval on this job yet.
            </p>
            <a
              href={whatsAppGarageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-green-300 bg-green-50 text-green-800 px-4 py-3 text-sm font-semibold"
            >
              <FaCommentDots /> Ask garage
            </a>
            <button
              type="button"
              onClick={() => setStep('landing')}
              className="block w-full rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700"
            >
              Back
            </button>
          </div>
        )}
        {step === 'parts' && currentPart && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
              <h2 className="text-lg font-bold text-slate-900 mb-1">{currentPart.plainName}</h2>
              {currentPart.diagnosisNote && (
                <p className="text-sm text-slate-500 mb-4">{currentPart.diagnosisNote}</p>
              )}

              <div className="space-y-3">
                {(currentPart.options || []).map((opt) => (
                  <button
                    key={opt.tier}
                    type="button"
                    onClick={() => handleSelectTier(opt.tier)}
                    className={`w-full text-left rounded-xl border-2 p-4 transition ${
                      selectedTier === opt.tier
                        ? 'border-red-500 bg-red-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-bold text-slate-900">{opt.tier}</span>
                      <span className="font-bold text-red-600">{formatINR(opt.price)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{opt.conditionLabel}</p>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-500">
                      {opt.warrantyDays > 0 && (
                        <span className="inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-full">
                          <FaShieldAlt className="text-emerald-500" />
                          {opt.warrantyDays}-day warranty
                        </span>
                      )}
                      {opt.brand && (
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{opt.brand}</span>
                      )}
                    </div>
                    {opt.photoUrl && (
                      <img
                        src={opt.photoUrl}
                        alt={currentPart.plainName}
                        className="mt-3 rounded-lg w-full max-h-40 object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <a
              href={whatsAppGarageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full rounded-xl border border-green-300 bg-green-50 text-green-800 py-3 text-sm font-semibold"
            >
              <FaCommentDots /> Ask garage
            </a>

            <button
              type="button"
              disabled={!selectedTier || payLoading}
              onClick={handleNextPart}
              className="w-full rounded-xl bg-red-600 disabled:bg-slate-300 text-white py-4 font-bold text-base"
            >
              {payLoading ? (
                <span className="inline-flex items-center gap-2">
                  <FaSpinner className="animate-spin" /> Saving…
                </span>
              ) : partIndex < parts.length - 1 ? (
                'Next'
              ) : (
                'Continue to payment'
              )}
            </button>
          </div>
        )}

        {/* CV-03 Summary + Payment */}
        {step === 'summary' && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm space-y-3">
              {summaryLines.map((line) => (
                <div key={line.plainName} className="flex justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{line.plainName}</p>
                    <p className="text-xs text-slate-500">
                      {line.tier} · {line.conditionLabel}
                      {line.warrantyDays ? ` · ${line.warrantyDays}d warranty` : ''}
                    </p>
                  </div>
                  <p className="font-semibold text-slate-900 shrink-0">{formatINR(line.price)}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm text-sm space-y-2">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span>{formatINR(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>GST (18%)</span>
                <span>{formatINR(totals.gst)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Platform fees</span>
                <span>{formatINR(totals.assurance ?? 20)}</span>
              </div>
              <div className="flex justify-between font-bold text-slate-900 text-base pt-2 border-t border-slate-100">
                <span>Grand total</span>
                <span>{formatINR(totals.grandTotal)}</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Payment method</p>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPaymentMethod(m.id)}
                    className={`rounded-xl py-3 text-sm font-semibold border-2 transition ${
                      paymentMethod === m.id
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-200 bg-white text-slate-700'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Cash on delivery is not available on this link.</p>
            </div>
          </div>
        )}
      </main>

      {/* Sticky pay CTA — CV-03 */}
      {step === 'summary' && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-4 safe-area-pb">
          <div className="max-w-lg mx-auto">
            <button
              type="button"
              disabled={payLoading}
              onClick={handlePay}
              className="w-full rounded-xl bg-red-600 disabled:bg-slate-400 text-white py-4 font-bold text-base shadow-lg"
            >
              {payLoading ? (
                <span className="inline-flex items-center gap-2 justify-center">
                  <FaSpinner className="animate-spin" /> Processing…
                </span>
              ) : (
                `Pay ${formatINR(totals.grandTotal)}`
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BridgeApproval;
